import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Sparkles, Send, ArrowUp, Loader2, Quote } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_LABEL = {
  opportunity: "Demand signal",
  bootstrapped: "Real revenue",
  gap: "Market map",
  category: "Category",
};

// Lightweight, safe formatter — renders headings, bold, and bullets from the
// model's plain-text markdown without pulling in a markdown lib.
function renderText(text) {
  const lines = (text || "").split("\n");
  const out = [];
  let bullets = null;
  const flush = () => {
    if (bullets) { out.push(<ul key={`u${out.length}`} className="list-disc pl-5 space-y-1 my-2">{bullets}</ul>); bullets = null; }
  };
  const inline = (s) => {
    const parts = s.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
      p.startsWith("**") && p.endsWith("**")
        ? <strong key={i} className="font-semibold text-zinc-900">{p.slice(2, -2)}</strong>
        : <span key={i}>{p}</span>);
  };
  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    if (/^#{1,4}\s+/.test(line)) {
      flush();
      out.push(<h4 key={idx} className="font-bold text-zinc-900 mt-3 mb-1">{inline(line.replace(/^#{1,4}\s+/, ""))}</h4>);
    } else if (/^\s*[-•]\s+/.test(line)) {
      const item = line.replace(/^\s*[-•]\s+/, "");
      (bullets = bullets || []).push(<li key={idx} className="text-zinc-700">{inline(item)}</li>);
    } else if (line === "---" || line === "") {
      flush();
      if (line === "") out.push(<div key={idx} className="h-2" />);
    } else {
      flush();
      out.push(<p key={idx} className="text-zinc-700 leading-relaxed my-1">{inline(line)}</p>);
    }
  });
  flush();
  return out;
}

export default function CopilotChat({ context = null, compact = false }) {
  const [messages, setMessages] = useState([]); // {role, content, sources}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [starters, setStarters] = useState([]);
  const sessionId = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    const ctxType = context?.type || "";
    axios.get(`${API}/copilot/starters`, { params: ctxType ? { ctx_type: ctxType } : {} })
      .then((r) => setStarters(r.data.starters || []))
      .catch(() => setStarters([]));
  }, [context?.type]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = useCallback(async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    try {
      const token = typeof window !== "undefined" ? localStorage.getItem("p2p_token") : null;
      const r = await axios.post(`${API}/copilot/chat`, {
        message: q, session_id: sessionId.current, context: context || undefined,
      }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      sessionId.current = r.data.session_id;
      setMessages((m) => [...m, { role: "assistant", content: r.data.answer, sources: r.data.sources || [], followups: r.data.followups || [] }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — I couldn't reach the research engine just now. Please try again in a moment.", sources: [] }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, context]);

  const empty = messages.length === 0;

  // "Build this →" from a Pain Point: auto-send a prefilled prompt once on mount.
  const prefillRan = useRef(false);
  useEffect(() => {
    if (prefillRan.current) return;
    const seed = typeof window !== "undefined" ? sessionStorage.getItem("pp_build_prompt") : null;
    if (seed) {
      prefillRan.current = true;
      sessionStorage.removeItem("pp_build_prompt");
      send(seed);
    }
  }, [send]);

  return (
    <div className={`flex flex-col ${compact ? "h-[520px]" : "h-[calc(100vh-180px)] min-h-[480px]"} bg-white rounded-2xl border border-zinc-200 overflow-hidden`} data-testid="copilot-view">
      {/* Header */}
      {!compact && (
        <div className="px-5 py-4 border-b border-zinc-100 bg-gradient-to-r from-violet-50 to-white flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-violet-600 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-bold text-zinc-900 leading-tight">Advisor</div>
            <div className="text-xs text-zinc-500">Ask anything — answers grounded in real demand, revenue & competitor data. No made-up numbers.</div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-4">
        {empty && (
          <div className="max-w-xl mx-auto text-center py-6" data-testid="copilot-empty">
            <div className="w-12 h-12 rounded-2xl bg-violet-100 flex items-center justify-center mx-auto mb-3">
              <Sparkles className="w-6 h-6 text-violet-600" />
            </div>
            <h3 className="text-lg font-bold text-zinc-900 mb-1">What do you want to figure out?</h3>
            <p className="text-sm text-zinc-500 mb-5">I'll answer from PrimePillar's real data and tell you honestly when I don't have the numbers.</p>
            <div className="grid gap-2">
              {starters.map((s, i) => (
                <button key={i} onClick={() => send(s)} data-testid={`copilot-starter-${i}`}
                  className="text-left text-sm px-4 py-3 rounded-xl border border-zinc-200 hover:border-violet-300 hover:bg-violet-50/50 text-zinc-700 transition-colors flex items-center justify-between gap-2 group">
                  <span>{s}</span>
                  <ArrowUp className="w-4 h-4 text-zinc-300 group-hover:text-violet-500 rotate-45 flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`copilot-message-${m.role}`}>
            <div className={m.role === "user"
              ? "max-w-[85%] bg-violet-600 text-white rounded-2xl rounded-br-sm px-4 py-2.5 text-sm"
              : "max-w-[90%] bg-zinc-50 border border-zinc-100 rounded-2xl rounded-bl-sm px-4 py-3 text-sm"}>
              {m.role === "user" ? <span>{m.content}</span> : (
                <>
                  <div className="copilot-prose">{renderText(m.content)}</div>
                  {m.sources && m.sources.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-zinc-200/70">
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-zinc-400 mb-1.5">
                        <Quote className="w-3 h-3" /> Grounded in
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {m.sources.slice(0, 8).map((s, j) => (
                          <span key={j} data-testid="copilot-source-chip"
                            className="text-[11px] px-2 py-1 rounded-md bg-white border border-zinc-200 text-zinc-600">
                            <span className="text-violet-500 font-medium">{SOURCE_LABEL[s.type] || s.type}</span>
                            {s.label ? ` · ${String(s.label).slice(0, 36)}` : ""}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {m.followups && m.followups.length > 0 && i === messages.length - 1 && !loading && (
                    <div className="mt-3 pt-3 border-t border-zinc-200/70" data-testid="copilot-followups">
                      <div className="text-[11px] font-semibold text-zinc-400 mb-1.5">Keep digging</div>
                      <div className="flex flex-wrap gap-1.5">
                        {m.followups.map((f, j) => (
                          <button key={j} onClick={() => send(f)} data-testid={`copilot-followup-${j}`}
                            className="text-left text-[12px] px-2.5 py-1.5 rounded-full border border-violet-200 text-violet-700 bg-violet-50/60 hover:bg-violet-100 transition-colors">
                            {f} <ArrowUp className="w-3 h-3 inline rotate-45 text-violet-400" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start" data-testid="copilot-loading">
            <div className="bg-zinc-50 border border-zinc-100 rounded-2xl rounded-bl-sm px-4 py-3 text-sm text-zinc-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-violet-500" /> Researching the data…
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-zinc-100 p-3 bg-white">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask about an idea, market, competitor, or how to start…"
            rows={1}
            data-testid="copilot-input"
            className="flex-1 resize-none max-h-32 px-4 py-2.5 rounded-xl border border-zinc-200 text-sm focus:outline-none focus:border-violet-400"
          />
          <button onClick={() => send()} disabled={loading || !input.trim()} data-testid="copilot-send"
            className="w-10 h-10 rounded-xl bg-violet-600 text-white flex items-center justify-center disabled:opacity-40 hover:bg-violet-700 transition-colors flex-shrink-0">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </div>
        <div className="text-[10px] text-zinc-400 mt-1.5 text-center">Grounded in PrimePillar data · not financial advice — verify before acting.</div>
      </div>
    </div>
  );
}
